import { useEffect, useRef, useCallback } from 'react';
import { Terminal as XTerm } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { io, type Socket } from 'socket.io-client';
import { Trash2, Circle } from 'lucide-react';
import { useTerminalStore } from '@/store';
import { cn } from '@/lib/utils';
import { getAuthToken } from '@/lib/auth';
import '@xterm/xterm/css/xterm.css';

interface TerminalProps {
  workspaceId: string;
}

export default function Terminal({ workspaceId }: TerminalProps) {
  const termRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<XTerm | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const { isConnected, setConnected, addMessage, clearMessages } =
    useTerminalStore();

  useEffect(() => {
    if (!termRef.current) return;

    const xterm = new XTerm({
      cursorBlink: true,
      fontSize: 13,
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
      theme: {
        background: '#1e1e1e',
        foreground: '#cccccc',
        cursor: '#ffffff',
        selectionBackground: '#264f78',
      },
      rows: 12,
    });

    const fitAddon = new FitAddon();
    xterm.loadAddon(fitAddon);
    xterm.open(termRef.current);
    fitAddon.fit();

    xtermRef.current = xterm;
    fitAddonRef.current = fitAddon;

    // Helper: fit xterm to container and notify server of new dimension
    const fitAndNotify = () => {
      fitAddon.fit();
      const dims = fitAddon.proposeDimensions();
      if (dims && socketRef.current?.connected) {
        socketRef.current.emit('resize', { cols: dims.cols, rows: dims.rows });
      }
    };

    // Connect to workspace terminal via socket.io
    // Server uses path '/ws/terminal' with events 'terminal:output' / 'terminal:input'
    const token = getAuthToken();
    const socket = io({
      path: '/ws/terminal',
      query: { workspaceId },
      auth: { token: token ?? '' },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      // Exponential backoff: 1s, 2s, 4s … up to 30s
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30_000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      xterm.writeln('\x1b[32m● Connected to workspace container\x1b[0m');
      addMessage({
        type: 'system',
        data: 'Connected to workspace',
        timestamp: Date.now(),
      });
      // Notify server of current terminal dimensions
      fitAndNotify();
    });

    socket.on('disconnect', (reason) => {
      setConnected(false);
      xterm.writeln(`\x1b[31m● Disconnected from workspace (${reason})\x1b[0m`);
    });

    socket.on('reconnect_attempt', (attempt: number) => {
      xterm.writeln(`\x1b[33m● Reconnecting… attempt ${attempt}\x1b[0m`);
    });

    socket.on('reconnect', () => {
      xterm.writeln('\x1b[32m● Reconnected\x1b[0m');
      fitAndNotify();
    });

    socket.on('connect_error', (err) => {
      xterm.writeln(`\x1b[31m● Connection error: ${err.message}\x1b[0m`);
    });

    socket.on('terminal:output', (data: string) => {
      xterm.write(data);
      addMessage({ type: 'stdout', data, timestamp: Date.now() });
    });

    socket.on('error', (err: { message: string }) => {
      xterm.writeln(`\x1b[31m● Error: ${err.message}\x1b[0m`);
    });

    // Send user input to server
    xterm.onData((data) => {
      socket.emit('terminal:input', data);
    });

    // Handle resize — fit xterm and notify PTY
    const resizeObserver = new ResizeObserver(() => {
      fitAndNotify();
    });
    resizeObserver.observe(termRef.current);

    return () => {
      resizeObserver.disconnect();
      socket.disconnect();
      xterm.dispose();
      xtermRef.current = null;
      socketRef.current = null;
    };
  }, [workspaceId, setConnected, addMessage]);

  const handleClear = useCallback(() => {
    xtermRef.current?.clear();
    clearMessages();
  }, [clearMessages]);

  return (
    <div className="flex flex-col h-full bg-[#1e1e1e]">
      {/* Terminal header */}
      <div className="flex items-center justify-between px-3 py-1 bg-[#252526] border-b border-[#3c3c3c]">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
            Terminal
          </span>
          <Circle
            className={cn(
              'w-2.5 h-2.5',
              isConnected ? 'text-green-500 fill-green-500' : 'text-red-500 fill-red-500'
            )}
          />
        </div>
        <button
          onClick={handleClear}
          className="p-1 rounded hover:bg-[#3c3c3c] text-gray-400 hover:text-gray-200"
          title="Clear terminal"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Terminal body */}
      <div ref={termRef} className="flex-1 min-h-0 px-1" />
    </div>
  );
}
